import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Divider
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import HomeIcon from '@mui/icons-material/Home';

export const NewAccountAsk = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    type: 'info',
    message: ''
  });

  // Handle form field changes
  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  // Validate email format
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Send email using EmailJS or a simple mailto link
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Validation
    if (!formData.email.trim() || !formData.message.trim()) {
      setAlert({
        show: true,
        type: 'error',
        message: 'Bitte füllen Sie alle Felder aus.'
      });
      return;
    }

    if (!validateEmail(formData.email)) {
      setAlert({
        show: true,
        type: 'error',
        message: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.'
      });
      return;
    }

    if (formData.message.length < 2) {
      setAlert({
        show: true,
        type: 'error',
        message: 'Die Nachricht muss mindestens 3 Zeichen lang sein.'
      });
      return;
    }

    setLoading(true);

    try {
      // Create email content
      const subject = encodeURIComponent('Neue Account-Anfrage');
      const body = encodeURIComponent(
        `Neue Account-Anfrage erhalten:\n\n` +
        `E-Mail: ${formData.email}\n\n` +
        `Nachricht:\n${formData.message}\n\n` +
        `Gesendet am: ${new Date().toLocaleString('de-DE')}`
      );
      
      const mailtoLink = `mailto:aleksander.ligeza-salamon@deutschebahn.com?subject=${subject}&body=${body}`;
      
      // Open email client
      window.location.href = mailtoLink;
      
      setAlert({
        show: true,
        type: 'success',
        message: 'Ihr E-Mail-Client wurde geöffnet. Bitte senden Sie die E-Mail ab, um Ihre Anfrage zu übermitteln.'
      });
      
      // Reset form after successful submission
      setFormData({
        email: '',
        message: ''
      });
      
    } catch (error) {
      console.error('Error sending email request:', error);
      setAlert({
        show: true,
        type: 'error',
        message: 'Fehler beim Öffnen des E-Mail-Clients. Bitte versuchen Sie es erneut oder kontaktieren Sie den Administrator direkt.'
      });
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setFormData({
      email: '',
      message: ''
    });
    setAlert({
      show: false,
      type: 'info',
      message: ''
    });
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <EmailIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" gutterBottom>
            Neuen Account beantragen
          </Typography>
        </Box>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Möchten Sie Zugang zu unserem System? Füllen Sie das folgende Formular aus, 
          um eine Account-Anfrage an den Administrator zu senden.
        </Typography>
        
        <Divider sx={{ mb: 3 }} />

        {alert.show && (
          <Alert severity={alert.type} sx={{ mb: 3 }}>
            {alert.message}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          {/* Email */}
          <TextField
            fullWidth
            label="Ihre E-Mail-Adresse"
            type="email"
            value={formData.email}
            onChange={handleInputChange('email')}
            margin="normal"
            required
            variant="outlined"
            helperText="Diese E-Mail-Adresse wird für Ihren Account verwendet"
            InputProps={{
              startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />

          {/* Message */}
          <TextField
            fullWidth
            label="Nachricht an den Administrator"
            value={formData.message}
            onChange={handleInputChange('message')}
            margin="normal"
            required
            multiline
            rows={3}
            variant="outlined"
            helperText="Beschreiben Sie kurz, warum Sie einen Account benötigen (mindestens 3 Zeichen)"
            placeholder="Beispiel: Ich bin neuer Mitarbeiter und benötige Zugang zu den Verkehrsinformationen für meine tägliche Routenplanung..."
          />

          {/* Information Box */}
          <Box 
            sx={{ 
              mt: 3, 
              p: 2, 
              backgroundColor: 'info.main', 
              color: 'info.contrastText',
              borderRadius: 1
            }}
          >
            <Typography variant="body2">
              <strong>Hinweis:</strong> Ihre Anfrage wird an den Administrator gesendet. 
              Sie erhalten eine Antwort, sobald Ihr Account erstellt wurde.
            </Typography>
          </Box>

          {/* Submit buttons */}
          <Box sx={{ m: 2, display: 'block', gap: 2, textAlign: 'center' }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              startIcon={<SendIcon />}
              sx={{ flex: 1, mb: 1 }}
            >
              {loading ? 'Wird gesendet...' : 'Anfrage senden'}
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={handleReset}
              disabled={loading}
            >
              Zurücksetzen
            </Button>
          </Box>

          {/* Contact Information */}
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Alternativ können Sie direkt eine E-Mail an{' '}
              <a href="mailto:aleksander.ligeza-salamon@deutschebahn.com" style={{ color: 'inherit' }}>
                mailto Administartor
              </a>{' '}
              senden.
            </Typography>
          </Box>

          {/* Home Button */}
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<HomeIcon />}
              onClick={() => navigate('/')}
              sx={{ minWidth: '150px' }}
            >
              Zur Startseite
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default NewAccountAsk;
